import React, { useEffect, useState, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import Camera, { IMAGE_TYPES } from 'react-html5-camera-photo';
import 'react-html5-camera-photo/build/css/index.css';
import PropTypes from 'prop-types';
import { ClosePopup, loaderSVG, SendMessage } from '../../rootAccess';
import EmojiButton from '../EmojiButton';
import { CameraIcon, Reset, Retake, Tick } from '../../../assets/images';
import { RETAKE } from '../../../Components/processENV';
import { dataURLtoFile } from '../../utils';
import './WebCamera.css';

const INITIAL_STATE = {
   error: null,
   photoTaken: false,
   imageData: null,
   loading: false,
};

const ERROR_TYPES = {
   PERMISSION: 'permission',
   CAMERA: 'camera',
};

const WebCameraBase = ({ onCapture, onClose, attachment = false }) => {
   const [state, setState] = useState(INITIAL_STATE);
   const [message, setMessage] = useState('');
   const textareaRef = useRef(null);
   const cursorPositionRef = useRef(null);

   const { error, photoTaken, imageData, loading } = state;

   const stopCameraStream = useCallback(() => {
      const video = document.querySelector('.react-html5-camera-photo > video');
      if (video?.srcObject) {
         video.srcObject.getTracks().forEach(track => track.stop());
         video.srcObject = null;
      }
   }, []);

   const handleTakePhoto = useCallback(
      dataUri => {
         if (!dataUri || dataUri.startsWith('data:,')) return;
         stopCameraStream();
         setState(prev => ({ ...prev, imageData: dataUri, photoTaken: true }));
      },
      [stopCameraStream],
   );

   const handleCameraError = useCallback(
      error => {
         stopCameraStream();
         const errorType = error?.name === 'NotAllowedError' ? ERROR_TYPES.PERMISSION : ERROR_TYPES.CAMERA;
         setState(prev => ({ ...prev, error: errorType }));
      },
      [stopCameraStream],
   );

   const handleConfirmPhoto = useCallback(() => {
      if (!imageData) return;
      setState(prev => ({ ...prev, loading: true }));
      const file = dataURLtoFile(imageData, 'profile-image.png');
      onCapture(file, message);
      onClose?.();
   }, [imageData, message, onCapture, onClose]);

   const handleRetake = useCallback(() => setState(INITIAL_STATE), []);

   const handleClose = useCallback(() => {
      onClose();
   }, [onClose]);

   const handleRetakeClick = useCallback(() => {
      handleRetake();
   }, [handleRetake]);

   useEffect(() => stopCameraStream, [stopCameraStream]);

   const pageTitle = useMemo(() => (photoTaken ? 'Preview' : 'Take Photo'), [photoTaken]);

   const errorConfig = useMemo(() => {
      if (!error) return null;
      return {
         title: error === ERROR_TYPES.PERMISSION ? 'Permission Denied' : 'Camera Not Found',
         message:
            error === ERROR_TYPES.PERMISSION
               ? 'Please allow camera access to take a photo.'
               : 'Your camera could not be accessed.',
      };
   }, [error]);

   const insertEmoji = useCallback(
      emoji => {
         const textarea = textareaRef.current;
         if (!textarea) return;
         const start = textarea.selectionStart;
         const end = textarea.selectionEnd;
         const textBefore = message.substring(0, start);
         const textAfter = message.substring(end);
         const newMessage = textBefore + emoji + textAfter;
         cursorPositionRef.current = start + emoji.length;
         setMessage(newMessage);
      },
      [message],
   );

   useLayoutEffect(() => {
      const textarea = textareaRef.current;
      if (textarea && document.activeElement === textarea && cursorPositionRef.current !== null) {
         textarea.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
         cursorPositionRef.current = null;
      }
   }, [message]);

   const autoResize = useCallback(() => {
      const el = textareaRef.current;
      if (el) {
         el.style.height = 'auto';
         el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
      }
   }, []);

   useEffect(() => {
      autoResize();
   }, [message, autoResize]);

   const handleKeyPress = useCallback(
      e => {
         if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleConfirmPhoto();
         }
      },
      [handleConfirmPhoto],
   );

   const ErrorPopup = useMemo(() => {
      if (!error) return null;
      return (
         <div className="camera-popup">
            <h4>{errorConfig.title}</h4>
            <i>
               <CameraIcon />
            </i>
            <p>{errorConfig.message}</p>
            <div className="popup-controls">
               <button type="button" className="btn-okay" onClick={onClose}>
                  Okay
               </button>
            </div>
         </div>
      );
   }, [error, errorConfig, onClose]);

   const CameraView = useMemo(
      () => (
         <div className="cameraview captured-picture">
            {photoTaken ? (
               <div id="CameraContainer">
                  <img src={imageData} alt="Captured" />
               </div>
            ) : (
               <Camera
                  imageType={IMAGE_TYPES.JPG}
                  isImageMirror
                  isSilentMode
                  sizeFactor={1}
                  onCameraError={handleCameraError}
                  onTakePhoto={handleTakePhoto}
                  isDisplayStartCameraError
               />
            )}
         </div>
      ),
      [photoTaken, imageData, handleCameraError, handleTakePhoto],
   );

   const Controls = useMemo(() => {
      if (!photoTaken || attachment) return null;
      return (
         <div
            className="popup-controls"
            style={{ display: 'flex', alignItems: 'center', top: '-2.538em', width: '23%' }}>
            {!loading && (
               <button onClick={handleRetake} title="Re-capture">
                  <Reset />
               </button>
            )}
            {loading ? (
               <img src={loaderSVG} alt="loading..." />
            ) : (
               <button title="Use Photo" onClick={handleConfirmPhoto}>
                  <Tick />
               </button>
            )}
         </div>
      );
   }, [photoTaken, attachment, loading, handleRetake, handleConfirmPhoto]);

   const Header = useMemo(
      () => (
         <div className="userprofile-header">
            <button onClick={photoTaken && attachment ? handleRetakeClick : handleClose}>
               {attachment && photoTaken && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white' }}>
                     <Retake />
                     {RETAKE}
                  </span>
               )}
            </button>
            <div style={{ flex: 1 }}>
               <h5>{pageTitle}</h5>
            </div>
            <button onClick={onClose}>
               <ClosePopup />
            </button>
         </div>
      ),
      [attachment, photoTaken, handleClose, handleRetakeClick, onClose, pageTitle],
   );

   const AttachmentFooter = useMemo(() => {
      if (!photoTaken || !attachment) return null;
      return (
         <div className="chat-footer">
            <div className="footer-container">
               <EmojiButton insertEmoji={insertEmoji} />
               <textarea
                  ref={textareaRef}
                  value={message}
                  id="camera-attachment"
                  name="camera-attachment"
                  className="footer-input"
                  placeholder="Type a message..."
                  rows={1}
                  autoFocus
                  onChange={e => setMessage(e.target.value)}
                  onKeyDown={handleKeyPress}
               />
               <button onClick={handleConfirmPhoto} disabled={loading} className="footer-icon" title="Send Photo">
                  {loading ? <img src={loaderSVG} alt="loading..." /> : <SendMessage />}
               </button>
            </div>
         </div>
      );
   }, [photoTaken, attachment, insertEmoji, message, loading, handleConfirmPhoto, handleKeyPress]);

   return (
      <div className={`camera-container ${attachment ? 'mediaAttachCamera' : ''}`}>
         {error ? (
            ErrorPopup
         ) : (
            <div className="camera-popup-visible">
               {Header}
               {CameraView}
               {Controls}
               {AttachmentFooter}
            </div>
         )}
      </div>
   );
};

WebCameraBase.propTypes = {
   onCapture: PropTypes.func.isRequired,
   onClose: PropTypes.func.isRequired,
   attachment: PropTypes.bool,
};

export const WebCamera = React.memo(WebCameraBase);
